import React, { Component } from "react";
import _get from "lodash/get";
import { PropTypes } from "prop-types";
import { Table, Label, Icon } from "semantic-ui-react";
import { getDisplayVal, invenioConfig } from "@config";

export default class DocumentItemBody extends Component {
  itemStatus = (item) => ({
    canCirculate: () =>
      invenioConfig.ITEMS.canCirculateStatuses.includes(item.status),
    isOnShelf: () => !item.circulation,
  });

  isItemOnLoan = (item) => {
    // Priority check: Use the _isOnLoan flag from backend if available
    if (Object.prototype.hasOwnProperty.call(item, "_isOnLoan")) {
      return item._isOnLoan;
    }

    // Check circulation state specifically
    if (item.circulation && item.circulation.state) {
      return item.circulation.state === "ITEM_ON_LOAN";
    }

    const hasCirculation = !!item.circulation;
    const canCirculate = this.itemStatus(item).canCirculate();
    const isOnShelf = this.itemStatus(item).isOnShelf();

    return hasCirculation && canCirculate && !isOnShelf;
  };

  getMediumDisplay = (item) => {
    const itemMedium = _get(item, "medium");
    const docMedium = _get(this.props.documentDetails, "metadata.medium");

    if (itemMedium) {
      return getDisplayVal("ITEMS.mediums", itemMedium) || itemMedium;
    }

    if (docMedium) {
      return getDisplayVal("DOCUMENTS.mediums", docMedium) || docMedium;
    }

    return "Not specified";
  };

  getRemarksContent = (item) => {
    const canCirculate = this.itemStatus(item).canCirculate();
    const isOnLoan = this.isItemOnLoan(item);
    const status = item.status;

    if (isOnLoan) {
      const dueDate = _get(item, "circulation.end_date");
      const dueDateDisplay = dueDate
        ? ` (Due: ${new Date(dueDate).toLocaleDateString()})`
        : "";

      return (
        <span className="item-on-loan italic bold">
          Currently checked out{dueDateDisplay}
        </span>
      );
    }

    switch (status) {
      case "MISSING":
        return (
          <span className="item-missing italic orange">
            Item reported missing from collection
          </span>
        );

      case "SCANNING":
        return (
          <span className="item-scanning italic yellow">
            Being digitized - temporarily unavailable
          </span>
        );

      case "FOR_REFERENCE_ONLY":
        return (
          <span className="item-reference italic grey">
            Use in library only - cannot be borrowed
          </span>
        );

      case "CAN_CIRCULATE":
        return (
          <span className="item-available italic green">
            Available for borrowing
          </span>
        );

      default:
        if (canCirculate) {
          return (
            <span className="item-available italic green">
              Available for borrowing
            </span>
          );
        }

        return (
          <span className="item-contact italic grey">
            Contact library for availability
          </span>
        );
    }
  };

  statusLabel = (item) => {
    const canCirculate = this.itemStatus(item).canCirculate();
    const isOnLoan = this.isItemOnLoan(item);
    const status = item.status;

    if (isOnLoan) {
      return (
        <Label color="blue" className="item-status-on-loan">
          <Icon name="user" />
          On Loan
        </Label>
      );
    }

    switch (status) {
      case "MISSING":
        return (
          <Label color="orange" className="item-status-missing">
            <Icon name="exclamation triangle" />
            Missing
          </Label>
        );

      case "SCANNING":
        return (
          <Label color="yellow" className="item-status-scanning">
            <Icon name="sync" />
            Scanning
          </Label>
        );

      case "FOR_REFERENCE_ONLY":
        return (
          <Label color="grey" className="item-status-reference">
            <Icon name="book" />
            Reference
          </Label>
        );

      case "CAN_CIRCULATE":
        return (
          <Label color="green" className="item-status-available">
            <Icon name="checkmark" />
            Available
          </Label>
        );

      default:
        if (canCirculate) {
          return (
            <Label color="green" className="item-status-available">
              <Icon name="checkmark" />
              Available
            </Label>
          );
        }

        return (
          <Label basic className="item-status-unknown">
            <Icon name="info circle" />
            {getDisplayVal("ITEMS.statuses", status) || status}
          </Label>
        );
    }
  };

  render() {
    const { items, shelfLink, documentDetails } = this.props;
    if (!items || items.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan="7" textAlign="center">
            No items available (items array is{" "}
            {items ? "empty" : "null/undefined"})
          </Table.Cell>
        </Table.Row>
      );
    }

    return items.map((item, index) => (
      <Table.Row key={item.pid || `item-${index}`}>
        <Table.Cell
          data-label="Barcode"
          className="document-item-table-itemCell"
        >
          <code className="item-barcode">
            {_get(item, "barcode", "No barcode")}
          </code>
        </Table.Cell>

        <Table.Cell
          data-label="Library Section"
          className="document-item-table-itemCell"
        >
          {item._internalLocationName ||
            _get(documentDetails, "metadata.sections", "Not specified")}
        </Table.Cell>

        <Table.Cell data-label="Shelf" className="document-item-table-itemCell">
          {shelfLink !== null
            ? shelfLink(item, documentDetails)
            : _get(item, "shelf", "Not specified")}
        </Table.Cell>

        <Table.Cell
          data-label="Call Number"
          className="document-item-table-itemCell"
        >
          {documentDetails?.metadata.call_number || "Not specified"}
        </Table.Cell>

        <Table.Cell
          data-label="Medium"
          className="document-item-table-itemCell"
        >
          {this.getMediumDisplay(item)}
        </Table.Cell>

        <Table.Cell data-label="Status">{this.statusLabel(item)}</Table.Cell>

        <Table.Cell
          data-label="Remarks"
          className="document-item-table-itemCell"
        >
          {this.getRemarksContent(item)}
        </Table.Cell>
      </Table.Row>
    ));
  }
}

DocumentItemBody.propTypes = {
  items: PropTypes.array.isRequired,
  documentDetails: PropTypes.object.isRequired,
  shelfLink: PropTypes.func,
};

DocumentItemBody.defaultProps = {
  shelfLink: null,
};
